import React, { useEffect, useMemo, useState } from "react";
import "./Leaderboard.css";
import { useDomain } from "@useDomain";
import api from "@api";
import { useNavigate } from "react-router-dom";
import { DEFAULT_LEADERBOARD_DISPLAY_CONFIG_BY_SPORT } from "../../../../utils/leaderboardSections.js";

function LeaderboardDefault({ page }) {
  const { domain, slug, organization } = useDomain();
  const [leaderboardData, setLeaderboardData] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [gameType, setGameType] = useState("all");
  const navigate = useNavigate();

  const rawSportName =
    organization?.sports?.[0]?.type ||
    organization?.sport?.type ||
    organization?.sport?.name ||
    organization?.sport ||
    "Basketball";

  const sportName = rawSportName
    ? rawSportName.charAt(0).toUpperCase() + rawSportName.slice(1).toLowerCase()
    : "Basketball";

  const allSections = useMemo(() => {
    return DEFAULT_LEADERBOARD_DISPLAY_CONFIG_BY_SPORT[sportName] || [];
  }, [sportName]);

  const visibleStatKeys = page?.content?.visibleStatKeys || [];

  const sections = useMemo(() => {
    return visibleStatKeys.length
      ? allSections.filter((section) => visibleStatKeys.includes(section.key))
      : allSections;
  }, [allSections, visibleStatKeys]);


  const filters = page?.content?.filters || {
    showAll: true,
    showRegular: true,
    showTournament: true,
  };
  const rawThemeMode =
    page?.content?.themeMode ||
    page?.pageSettings?.content?.themeMode ||
    organization?.theme_mode ||
    "light";

  const leaderboardTheme =
    String(rawThemeMode).trim().toLowerCase() === "dark" ? "dark" : "light";


  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoaded(false);

        const responses = await Promise.all(
          sections.map((section) =>
            api.get(section.endpoint, {
              params: {
                ...(slug ? { slug } : { domain }),
                type: gameType,
              },
            })
          )
        );

        const mappedData = {};
        sections.forEach((section, index) => {
          mappedData[section.key] = responses[index].data || [];
        });

        setLeaderboardData(mappedData);
        setLoaded(true);
      } catch (err) {
        console.error("Failed to load records", err);
        setLoaded(true);
      }
    };

    if (sections.length) {
      fetchRecords();
    } else {
      setLeaderboardData({});
      setLoaded(true);
    }
  }, [domain, slug, gameType, sections]);

  const handleRowClick = (section, row) => {
    if (section.type === "player") {
      navigate(slug ? `/${slug}/players/${row.id}` : `/players/${row.id}`);
      return;
    }

    if (section.type === "team") {
      navigate(slug ? `/${slug}/teams/${row.teamId}` : `/teams/${row.teamId}`);
    }
  };

  return (
    <div className={`leader-board-container leaderboard-theme-${leaderboardTheme}`}>
      <div className="leaderboard-toggle">
        {filters.showAll && (
          <button
            className={gameType === "all" ? "active" : ""}
            onClick={() => setGameType("all")}
          >
            All
          </button>
        )}

        {filters.showRegular && (
          <button
            className={gameType === "regular" ? "active" : ""}
            onClick={() => setGameType("regular")}
          >
            Regular
          </button>
        )}

        {filters.showTournament && (
          <button
            className={gameType === "tournament" ? "active" : ""}
            onClick={() => setGameType("tournament")}
          >
            Tournament
          </button>
        )}
      </div>

      {loaded && (
        <section className="current-leader-section">
          {sections.map((section) => {
            const rows = leaderboardData[section.key] || [];

            return (
              <div className="leaderboard-section" key={section.key}>
                <p className="leaderboard-title-name">{section.title}</p>

                <div className="leaderboard-scroll">
                  <table className="leaderboard-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>{section.type === "team" ? "Team" : "Player"}</th>
                        <th>{section.valueLabel}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {rows.map((row, i) => (
                        <tr
                          key={section.type === "team" ? row.teamId : row.id}
                          onClick={() => handleRowClick(section, row)}
                        >
                          <td>{i + 1}</td>
                          <td>
                            {section.type === "team"
                              ? row.name
                              : `${row.firstName} ${row.lastName}`}
                          </td>
                          <td>{row[section.valueKey]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}

export default LeaderboardDefault;